import React, { useState, useEffect } from 'react';
import { usersAPI, groupsAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiEdit2, FiTrash2, FiLock, FiPlus, FiBell } from 'react-icons/fi';
import UserModal from './UserModal';
import AdminNotificationSettings from './AdminNotificationSettings';
import './Settings.css';

const UserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notifUser, setNotifUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    console.log('[UserManagement.jsx] mounted');
    return () => console.log('[UserManagement.jsx] unmounted');
  }, []);


  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, groupsResponse] = await Promise.all([
        usersAPI.getAll(),
        groupsAPI.getAll()
      ]);
      
      if (usersResponse.data.success) {
        setUsers(usersResponse.data.users);
      }
      
      if (groupsResponse.data.success) {
        setGroups(groupsResponse.data.groups);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(t('messages.error_load', 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(t('users.confirm_delete', `Are you sure you want to delete user "${user.username}"?`))) {
      return;
    }
    
    try {
      const response = await usersAPI.delete(user.user_id);
      if (response.data.success) {
        alert(t('messages.success_delete', 'User deleted successfully'));
        loadData();
      }
    } catch (err) {
      alert(err.response?.data?.error || t('messages.error_delete', 'Failed to delete user'));
    }
  };

  const handleResetPassword = async (user) => {
    const newPassword = prompt(t('users.enter_new_password', `Enter new password for ${user.username}:\n(Minimum 6 characters)`));
    
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      alert(t('users.password_min_length', 'Password must be at least 6 characters long'));
      return;
    }
    
    try {
      const response = await usersAPI.resetPassword(user.user_id, { newPassword });
      if (response.data.success) {
        alert(t('users.password_reset_success', `Password reset successfully for ${user.username}`));
      }
    } catch (err) {
      alert(err.response?.data?.error || t('messages.error_save', 'Failed to reset password'));
    }
  };

  const handleModalClose = (shouldReload) => {
    setShowModal(false);
    setSelectedUser(null);
    if (shouldReload) {
      loadData();
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.username.toLowerCase().includes(search) ||
      user.full_name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return <div className="loading">{t('loading', 'Loading users...')}</div>;
  }

  if (error) {
    return (
      <div className="error">
        <p>{error}</p>
        <button onClick={loadData}>{t('retry', 'Retry')}</button>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div>
          <h2>{t('users.title', 'User Management')}</h2>
          <p className="settings-subtitle">{t('users.subtitle', 'Manage system users and their access')}</p>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          <FiPlus size={16} style={{ marginRight: '6px' }} />
          {t('users.create', 'Create User')}
        </button>
      </div>

      <div className="settings-filters">
        <input
          type="text"
          placeholder={t('search.users', 'Search users...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <span className="result-count">
          {filteredUsers.length} {t('users.found', 'users found')}
        </span>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>{t('users.username', 'Username')}</th>
              <th>{t('users.full_name', 'Full Name')}</th>
              <th>{t('users.email', 'Email')}</th>
              <th>{t('users.groups', 'Grupos')}</th>
              <th>{t('users.type', 'Type')}</th>
              <th>{t('users.status', 'Status')}</th>
              <th>{t('users.last_login', 'Last Login')}</th>
              <th>{t('actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.user_id}>
                <td className="username">{user.username}</td>
                <td>{user.full_name || '-'}</td>
                <td>{user.email || '-'}</td>
                <td>
                  {(user.groups && user.groups.length > 0) ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {user.groups.map(g => (
                        <span key={g.group_id} style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px',
                          borderRadius: 12, background: '#f0fdf9',
                          color: 'var(--primary)', border: '1px solid var(--primary)',
                          whiteSpace: 'nowrap',
                        }}>
                          {g.group_name}
                        </span>
                      ))}
                    </div>
                  ) : '-'}
                </td>
                <td>
                  <span className={`badge badge-${user.user_type}`}>
                    {user.user_type || 'user'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                    {user.is_active ? t('status.active', 'Active') : t('status.inactive', 'Inactive')}
                  </span>
                </td>
                <td>
                  {user.last_login 
                    ? new Date(user.last_login).toLocaleString()
                    : t('users.never_logged', 'Never')}
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => handleEdit(user)}
                      className="btn-action btn-edit"
                      title={t('edit', 'Edit user')}
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button 
                      onClick={() => setNotifUser(user)}
                      className="btn-action btn-notif"
                      title={t('notifications.manage_btn', 'Notifications')}
                    >
                      <FiBell size={16} />
                    </button>
                    <button 
                      onClick={() => handleResetPassword(user)}
                      className="btn-action btn-reset"
                      title={t('users.reset_password', 'Reset password')}
                    >
                      <FiLock size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(user)}
                      className="btn-action btn-delete"
                      title={t('delete', 'Delete user')}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <UserModal
          user={selectedUser}
          groups={groups}
          onClose={handleModalClose}
        />
      )}

      {notifUser && (
        <AdminNotificationSettings
          user={notifUser}
          onClose={() => setNotifUser(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;